import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getDashboardUsers, RevenueDailyTrend, RevenueOverview, RevenueSources } from '../../services/dashboard.services';

const COLORS = ['#28a745', '#ffc107', '#dc3545']; // Green (Consultations), Amber (Remedies), Red (Puja)

const RevenueDashboard = () => {
  const [overview, setOverview] = useState({});
  const [trend, setTrend] = useState([]);
  const [sources, setSources] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendRes, sourcesRes] = await Promise.all([
        RevenueOverview(period),
        RevenueDailyTrend(period),
        RevenueSources(period),
      ]);

      // Overview Data
      setOverview(overviewRes.data || {});

      // Trend Data - Multi-category formatting
      if (trendRes?.data?.labels && Array.isArray(trendRes.data.labels)) {
        const formattedTrend = trendRes.data.labels.map((label, i) => ({
          label, // e.g., "02 Dec"
          // Consultations
          consultation_revenue: trendRes.data.consultations?.revenue?.[i] ?? 0,
          consultation_count: trendRes.data.consultations?.count?.[i] ?? 0,
          // Remedies
          remedies_revenue: trendRes.data.remedies?.revenue?.[i] ?? 0,
          remedies_count: trendRes.data.remedies?.count?.[i] ?? 0,
          // Puja
          puja_revenue: trendRes.data.puja?.revenue?.[i] ?? 0,
          puja_count: trendRes.data.puja?.count?.[i] ?? 0,
        }));
        setTrend(formattedTrend);
      } else {
        setTrend([]);
      }

      // Revenue Sources Pie Chart
      const sourcesData = [
        { name: 'Consultations', value: sourcesRes.data?.consultations || 0 },
        { name: 'Remedies', value: sourcesRes.data?.remedies || 0 },
        { name: 'Puja', value: sourcesRes.data?.puja || 0 },
      ].filter(item => item.value > 0); // Hide zero values

      setSources(sourcesData);
    } catch (err) {
      console.error("Error fetching revenue dashboard data:", err);
      setTrend([]);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="placeholder-glow">
          <h1 className="placeholder col-4 mb-4"></h1>
        </div>
        <div className="row g-4 mb-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="col-md-3">
              <div className="card placeholder-glow">
                <div className="card-body">
                  <div className="placeholder col-6 mb-2"></div>
                  <div className="placeholder col-8" style={{ height: '40px' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card placeholder-glow">
              <div className="card-body">
                <div className="placeholder col-4 mb-3"></div>
                <div className="placeholder" style={{ height: '400px' }}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card placeholder-glow">
              <div className="card-body">
                <div className="placeholder col-4 mb-3"></div>
                <div className="placeholder" style={{ height: '400px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h1 className="mb-4 fw-bold">Admin Revenue Dashboard</h1>

      {/* Period Selector */}
      <div className="mb-4">
        <select
          className="form-select w-auto d-inline-block"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="all_time">All Time</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div className="row g-4 mb-5">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted">Total Revenue</h5>
              <p className="card-text fs-2 fw-bold text-success">
                ₹{overview.totalRevenue?.toLocaleString() ?? 0}
              </p>
              <small className="text-muted">
                {overview.dateRange?.from ?? '--'} to {overview.dateRange?.to ?? '--'}
              </small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted">Platform Commission</h5>
              <p className="card-text fs-2 fw-bold text-primary">
                ₹{overview.platformCommission?.toLocaleString() ?? 0}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted">Astrologer Payout</h5>
              <p className="card-text fs-2 fw-bold text-danger">
                ₹{overview.astrologerPayout?.toLocaleString() ?? 0}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted">Total Sessions</h5>
              <p className="card-text fs-2 fw-bold">{overview.totalBookings ?? 0}</p>
              <small className="text-muted">Avg Order: ₹{overview.avgOrderValue ?? 0}</small>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-5">
        {/* Revenue Trend Line Chart */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Revenue & Orders Trend</h5>
            </div>
            <div className="card-body">
              {trend.length === 0 ? (
                <div className="text-center py-5 text-muted">No trend data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={420}>
                  <LineChart data={trend} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="label"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      yAxisId="revenue"
                      orientation="left"
                      tickFormatter={(v) => `₹${v.toLocaleString()}`}
                      label={{ value: 'Revenue (₹)', angle: -90, position: 'insideLeft' }}
                    />
                    <YAxis
                      yAxisId="count"
                      orientation="right"
                      label={{ value: 'Orders Count', angle: 90, position: 'insideRight' }}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name.includes('revenue')
                          ? `₹${Number(value).toLocaleString()}`
                          : value
                      }
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend verticalAlign="top" height={50} />

                    {/* Revenue Lines */}
                    <Line yAxisId="revenue" type="monotone" dataKey="consultation_revenue" stroke="#28a745" strokeWidth={3} name="Consultation Revenue" dot={{ r: 4 }} />
                    <Line yAxisId="revenue" type="monotone" dataKey="remedies_revenue" stroke="#ffc107" strokeWidth={3} name="Remedies Revenue" dot={{ r: 4 }} />
                    <Line yAxisId="revenue" type="monotone" dataKey="puja_revenue" stroke="#dc3545" strokeWidth={3} name="Puja Revenue" dot={{ r: 4 }} />

                    {/* Count Lines */}
                    <Line yAxisId="count" type="monotone" dataKey="consultation_count" stroke="#17a2b8" strokeWidth={2} name="Consultation Orders" />
                    <Line yAxisId="count" type="monotone" dataKey="remedies_count" stroke="#6f42c1" strokeWidth={2} name="Remedies Orders" />
                    <Line yAxisId="count" type="monotone" dataKey="puja_count" stroke="#fd7e14" strokeWidth={2} name="Puja Orders" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Revenue Sources Pie Chart - Fixed Labels */}
<div className="col-lg-4">
  <div className="card border-0 shadow-sm h-100">
    <div className="card-header bg-light">
      <h5 className="mb-0">Revenue by Source</h5>
    </div>
    <div className="card-body d-flex align-items-center justify-content-center">
      {sources.length === 0 ? (
        <p className="text-muted">No revenue data</p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={sources}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={40} // Optional: makes it a donut for better look
              paddingAngle={3}
              labelLine={true}
              label={(props) => {
                const { cx, cy, midAngle, outerRadius, name, value } = props;
                const RADIAN = Math.PI / 180;
                const radius = outerRadius + 30; // Distance from center
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                  <text
                    x={x}
                    y={y}
                    fill="#333"
                    textAnchor={x > cx ? "start" : "end"}
                    dominantBaseline="central"
                    fontSize="12"
                    fontWeight="500"
                  >
                    {`${name}`}
                    <tspan x={x} dy="1.2em" fontWeight="bold" fill="#198754">
                      ₹{value.toLocaleString()}
                    </tspan>
                  </text>
                );
              }}
            >
              {sources.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => `₹${Number(value).toLocaleString()}`}
              contentStyle={{ backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <Legend
              verticalAlign="bottom"
              height={50}
              formatter={(value) => (
                <span style={{ fontWeight: "500" }}>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  </div>
</div>
      </div>

      {/* User Tables */}
      <div className="row g-4">
        <UserTable role="user" title="Recent Customers" />
        <UserTable role="astrologer" title="Active Astrologers" />
      </div>
    </div>
  );
};

// UserTable Component (unchanged, just minor styling)
const UserTable = ({ role, title }) => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    getDashboardUsers(role, page)
      .then(res => {
        setUsers(res.data.users || []);
        setTotalPages(res.data.pages || 1);
      })
      .catch(err => console.error(err));
  }, [role, page]);

  return (
    <div className="col-lg-6">
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-light">
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="table-responsive mb-4">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Wallet</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map(u => (
                  <tr key={u._id}>
                    <td>{u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}</td>
                    <td>{u.phone_number || u.email}</td>
                    <td className="fw-bold">₹{u.wallet_balance?.toLocaleString() ?? 0}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="card-footer bg-white border-top-0 pt-3 pb-3">
            <nav aria-label="Page navigation">
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button>
                </li>
                <li className="page-item disabled"><span className="page-link">{page} / {totalPages}</span></li>
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueDashboard;